/**
 * Réassort sur mesure (K&J B2B) : bloc de thème blocks/kj-reassort.liquid.
 * Les lignes (recettes des dernières commandes du client) sont rendues en Liquid, avec pour chacune la quantité de
 * la dernière commande (data-last) et la moyenne des dernières commandes (data-hab). Ici : quantités choisies par
 * mode, gestes « Tout vendu / Une partie / Il m'en reste », sélecteur, totaux, jauge franco, ajout groupé au panier.
 * Spec : specs/theme/REASSORT.md
 */
import { CartUpdatedEvent, ThemeEvents } from '@theme/events';

const euros = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });
const eur = (centimes) => euros.format(centimes / 100);

/* K&J (29/09/2026, entrée 252) : toute écriture panier passe par la file unique (layout/theme.liquid),
   une requête à la fois, dans l'ordre des clics. Sans la file (page sans le layout) : exécution directe. */
const kjFile = (job) => (window.kjPanierFile ? window.kjPanierFile.run(job) : job());

class KjReassort extends HTMLElement {
  connectedCallback() {
    this.lignes = [...this.querySelectorAll('[data-ligne]')];
    if (!this.lignes.length) return;
    this.mode = 'last';
    this.choisirCommande();
    this.q = { last: {}, hab: {} };
    this.lignes.forEach((l) => {
      const v = l.dataset.variant;
      const dispo = l.dataset.dispo === 'true';
      this.q.last[v] = dispo ? parseInt(l.dataset.last, 10) || 0 : 0;
      this.q.hab[v] = dispo ? parseInt(l.dataset.hab, 10) || 0 : 0;
    });
    this.addEventListener('click', (e) => this.clic(e));
    // Barre basse mobile : visible seulement quand le bloc est à l'écran (pas sur le pied de page).
    this.obs = new IntersectionObserver(([e]) => this.classList.toggle('is-visible', e.isIntersecting), { rootMargin: '0px 0px -120px 0px' });
    this.obs.observe(this);
    this.rendre();
  }

  // « Réassortir » depuis une commande précise de Mon espace pro : /pages/reassortir?commande=1007
  choisirCommande() {
    const n = (new URLSearchParams(window.location.search).get('commande') || '').replace('#', '').trim();
    const liste = (this.dataset.commandes || '').split(';').filter(Boolean).map((x) => x.split(':'));
    const cmd = liste.find((x) => x[0] === n);
    if (!cmd) return;
    this.lignes.forEach((l) => {
      const m = (l.dataset.cmds || '').split(';').map((x) => x.split(':')).find((x) => x[0] === n);
      l.dataset.last = m ? m[1] : '0';
      l.dataset.off = m ? m[2] : '0';
      const off = l.querySelector('[data-ref-off]');
      if (off) off.textContent = (this.dataset.texteDont || '').replace('#', l.dataset.off);
    });
    const src = this.querySelector('[data-source-last]');
    if (src) src.textContent = (this.dataset.texteSource || '').replace('[commande]', `#${n}`).replace('[date]', cmd[1]);
    const onglet = this.querySelector('[data-mode="last"]');
    if (onglet && liste[0][0] !== n && this.dataset.ongletCommande) onglet.textContent = this.dataset.ongletCommande.replace('[commande]', `#${n}`);
  }

  base(l) {
    return parseInt(this.mode === 'last' ? l.dataset.last : l.dataset.hab, 10) || 0;
  }

  clic(e) {
    const onglet = e.target.closest('[data-mode]');
    if (onglet) {
      this.mode = onglet.dataset.mode;
      this.rendre();
      return;
    }
    const action = e.target.closest('[data-action]');
    if (action) {
      this.lignes.forEach((l) => {
        if (l.dataset.dispo !== 'true') return;
        this.q[this.mode][l.dataset.variant] = action.dataset.action === 'tout' ? this.base(l) : 0;
      });
      this.rendre();
      return;
    }
    if (e.target.closest('[data-ajouter]')) {
      this.ajouter(e.target.closest('[data-ajouter]'));
      return;
    }
    const ligne = e.target.closest('[data-ligne]');
    if (!ligne) return;
    const v = ligne.dataset.variant;
    const b = this.base(ligne);
    const choix = e.target.closest('[data-choix]');
    if (choix) {
      const c = choix.dataset.choix;
      this.q[this.mode][v] = c === 'tout' ? b : c === 'partie' ? Math.max(1, Math.ceil(b / 2)) : 0;
      this.rendre();
      return;
    }
    const pas = e.target.closest('[data-qte]');
    if (pas) {
      this.q[this.mode][v] = Math.max(0, (this.q[this.mode][v] || 0) + parseInt(pas.dataset.qte, 10));
      this.rendre();
    }
  }

  rendre() {
    const m = this.mode;
    this.querySelectorAll('[data-mode]').forEach((o) => o.setAttribute('aria-selected', String(o.dataset.mode === m)));
    this.querySelector('[data-source-last]')?.toggleAttribute('hidden', m !== 'last');
    this.querySelector('[data-source-hab]')?.toggleAttribute('hidden', m !== 'hab');
    let refs = 0, colis = 0, sous = 0;
    this.lignes.forEach((l) => {
      const b = this.base(l);
      l.hidden = b === 0;
      l.querySelector('[data-ref-qty]').textContent = b;
      l.querySelector('[data-ref-label-last]')?.toggleAttribute('hidden', m !== 'last');
      l.querySelector('[data-ref-label-hab]')?.toggleAttribute('hidden', m !== 'hab');
      l.querySelector('[data-ref-off]')?.toggleAttribute('hidden', m !== 'last' || l.dataset.off === '0');
      if (l.dataset.dispo !== 'true' || l.hidden) return;
      const q = this.q[m][l.dataset.variant] || 0;
      const prix = parseInt(l.dataset.prix, 10) || 0;
      const choix = q === 0 ? 'reste' : q >= b ? 'tout' : 'partie';
      l.querySelectorAll('[data-choix]').forEach((c) => c.setAttribute('aria-pressed', String(c.dataset.choix === choix)));
      l.querySelector('[data-qte-valeur]').textContent = q;
      l.querySelector('[data-ligne-total]').textContent = q > 0 ? eur(q * prix) : '—';
      l.classList.toggle('is-zero', q === 0);
      if (q > 0) { refs += 1; colis += q; sous += q * prix; }
    });
    this.querySelectorAll('[data-sous-groupe]').forEach((sg) => {
      sg.hidden = ![...sg.querySelectorAll('[data-ligne]')].some((l) => !l.hidden);
    });
    this.querySelectorAll('[data-groupe]').forEach((g) => {
      const visibles = [...g.querySelectorAll('[data-ligne]')].filter((l) => !l.hidden);
      g.hidden = visibles.length === 0;
      const n = visibles.reduce((s, l) => s + (l.dataset.dispo === 'true' ? this.q[m][l.dataset.variant] || 0 : 0), 0);
      const r = g.querySelector('[data-groupe-resume]');
      if (r) r.textContent = n > 0 ? `${n} colis à reprendre` : 'rien à reprendre';
    });
    const remisePct = parseFloat(this.dataset.remise) || 0;
    const remise = Math.round((sous * remisePct) / 100);
    const set = (sel, txt) => { const el = this.querySelector(sel); if (el) el.textContent = txt; };
    set('[data-nb-refs]', refs);
    set('[data-nb-colis]', colis);
    set('[data-sous-total]', eur(sous));
    set('[data-remise-montant]', '−' + eur(remise));
    set('[data-total]', eur(sous - remise));
    const seuil = parseInt(this.dataset.franco, 10) || 12;
    const manque = Math.max(0, seuil - colis);
    const franco = manque > 0 ? (this.dataset.texteFranco || '').replace('#', manque) : this.dataset.texteFrancoOk || '';
    const remiseTxt = remisePct > 0 && this.dataset.texteRemiseIncluse ? this.dataset.texteRemiseIncluse.replace('#', String(remisePct).replace('.', ',')) : '';
    // Sur mobile, la mention de remise suit le texte du franco : « Livraison offerte · remise pro 10 % incluse. »
    set('[data-franco-texte]', remiseTxt ? franco.replace(/\.\s*$/, '') : franco);
    set('[data-remise-mobile]', remiseTxt ? remiseTxt.replace(/\.?\s*$/, '.') : '');
    set('[data-resume-mobile]', (this.dataset.texteResume || '# colis · % recettes').replace('#', colis).replace('%', refs));
    set('[data-total-mobile]', `${eur(sous - remise)} HT`);
    const barre = this.querySelector('[data-franco-barre]');
    if (barre) barre.style.width = `${Math.min(100, Math.round((colis / seuil) * 100))}%`;
    const btn = this.querySelector('[data-ajouter]');
    if (btn) {
      const label = (this.dataset.texteCta || '').replace('#', colis);
      const cible = btn.querySelector('.button__text, span') || btn;
      cible.textContent = colis > 0 ? label : label.replace(/\s*·.*$/, '');
      btn.disabled = colis === 0;
    }
    this.colis = colis;
  }

  // 30/10 (entrée 534) : plus de page panier. Après l'ajout, le tiroir est re-rendu (même section que les autres
  // écritures, via kjPanierFile.sections) puis ouvert ; l'icône, les compteurs et l'éco suivent l'événement cart:updated.
  // Sans tiroir sur la page (cas qui ne doit pas arriver) : repli sur la page panier, comme avant.
  async ouvrirTiroir(btn) {
    const tiroir = document.querySelector('cart-drawer');
    if (!tiroir) { window.location.href = this.dataset.cartUrl || '/cart'; return; }
    try {
      const ids = window.kjPanierFile?.sections ? window.kjPanierFile.sections(['auto-gifts']) : [tiroir.dataset.sectionId];
      const [sections, panier] = await Promise.all([
        fetch(`${window.location.pathname}?sections=${ids.join(',')}`).then((r) => r.json()),
        fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()),
      ]);
      document.dispatchEvent(new CartUpdatedEvent(panier, { itemCount: panier.item_count, source: 'kj-reassort', sections }));
      document.dispatchEvent(new Event(ThemeEvents.cartOpen));
    } catch (e) {
      window.location.href = this.dataset.cartUrl || '/cart';
      return;
    }
    if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
  }

  async ajouter(btn) {
    const m = this.mode;
    const items = this.lignes
      .filter((l) => !l.hidden && l.dataset.dispo === 'true' && (this.q[m][l.dataset.variant] || 0) > 0)
      .map((l) => ({ id: parseInt(l.dataset.variant, 10), quantity: this.q[m][l.dataset.variant] }));
    if (!items.length) return;
    const statut = this.querySelector('[data-statut]');
    btn.disabled = true;
    btn.classList.add('is-loading');
    const url = `${this.dataset.cartAddUrl || '/cart/add'}.js`;
    const post = (lot) => kjFile(() => fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lot }),
    }));
    try {
      const res = await post(items);
      if (!res.ok) {
        // Une recette refusée (retirée du site entre-temps) fait échouer tout le lot : on ajoute alors une par une.
        let echecs = 0;
        for (const it of items) {
          const r = await post([it]);
          if (!r.ok) echecs += 1;
        }
        if (echecs === items.length) throw new Error((this.dataset.texteEchec || '#').replace('#', echecs));
        if (echecs > 0) {
          // Le reste est bien au panier : on prévient, puis on y va.
          if (statut) statut.textContent = (this.dataset.texteEchec || '#').replace('#', echecs);
          setTimeout(() => this.ouvrirTiroir(btn), 2500);
          return;
        }
      }
      await this.ouvrirTiroir(btn);
    } catch (err) {
      if (statut) statut.textContent = err.message;
      btn.disabled = false;
      btn.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('kj-reassort')) customElements.define('kj-reassort', KjReassort);
